import { Phone, Mail, MapPin, Clock } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import ContactForm from "../components/ContactForm";
import { useSEO } from "../hooks/useSEO";
import { site } from "../data/site";

export default function Contact() {
  useSEO({
    title: "Contact — Embek Group, jardinier à Charleroi, Namur et Mons",
    description:
      "Contactez Embek Group à Charleroi, Namur et Mons : téléphone, e-mail, adresse, zone d'intervention et horaires. Formulaire de contact et carte pour toute demande de jardinage ou d'aménagement extérieur.",
  });

  const infos = [
    { icon: Phone, label: "Téléphone", value: site.phone, href: site.phoneHref, testid: "contact-info-phone" },
    { icon: Mail, label: "E-mail", value: site.email, href: `mailto:${site.email}`, testid: "contact-info-email" },
    { icon: MapPin, label: "Zone d'intervention", value: site.zone, testid: "contact-info-zone" },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Contact"
        title="Parlons de votre projet"
        description="Une question, un besoin, un projet d'aménagement ? Notre équipe vous répond rapidement."
      />

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-[1fr_1.2fr] gap-12">
          <div>
            <SectionHeading eyebrow="Nos coordonnées" title="Nous sommes à votre écoute" />
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {infos.map((info, i) => (
                <Reveal key={info.label} delay={i * 0.06} className="h-full">
                  <div className="h-full bg-white rounded-2xl border border-forest/10 p-6 shadow-sm" data-testid={info.testid}>
                    <span className="w-10 h-10 rounded-full bg-forest/8 text-moss flex items-center justify-center">
                      <info.icon size={18} />
                    </span>
                    <h3 className="mt-4 text-[11px] uppercase tracking-[0.22em] font-semibold text-moss">{info.label}</h3>
                    {info.href ? (
                      <a href={info.href} className="mt-1.5 block text-[15px] font-medium text-forest hover:text-moss transition-colors break-all">
                        {info.value}
                      </a>
                    ) : (
                      <p className="mt-1.5 text-[15px] font-medium text-forest">{info.value}</p>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.2} className="mt-4">
              <div className="bg-forest rounded-2xl p-7 text-sand" data-testid="contact-horaires">
                <div className="flex items-center gap-3">
                  <Clock size={18} className="text-moss" />
                  <h3 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-moss">Horaires</h3>
                </div>
                <ul className="mt-4 space-y-2">
                  {site.horaires.map((h) => (
                    <li key={h.jours} className="flex justify-between text-sm text-sand/80 border-b border-sand/10 pb-2 last:border-0 last:pb-0">
                      <span>{h.jours}</span>
                      <span className="font-semibold text-sand">{h.heures}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <ContactForm />
          </Reveal>
        </div>
      </section>

      {/* Zone + carte */}
      <section className="bg-sand-mid py-16 lg:py-24" data-testid="zone-section-contact">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <SectionHeading
            align="center"
            eyebrow="Zone d'intervention"
            title="Nous intervenons dans votre région"
            description="Nos équipes se déplacent autour de Charleroi, Namur et Mons pour l'entretien et l'aménagement de vos espaces verts."
          />
          <Reveal className="mt-10">
            <div className="max-w-4xl mx-auto space-y-6" data-testid="zone-communes">
              {site.zones.map((z) => (
                <div key={z.name}>
                  <h4 className="flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-semibold text-moss mb-3">
                    <MapPin size={14} /> {z.name}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {z.communes.map((c, i) => (
                      <span key={i} className="px-3.5 py-1.5 rounded-full border border-forest/15 bg-white text-[12.5px] font-medium text-forest/80">
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal className="mt-10">
            <div className="rounded-3xl overflow-hidden border border-forest/10 shadow-lg shadow-forest/10 h-[420px]">
              <iframe
                title="Carte de la zone d'intervention d'Embek Group"
                src="https://www.google.com/maps?q=Wallonie,+Belgique&z=8&hl=fr&output=embed"
                className="w-full h-full grayscale-[0.3]"
                loading="lazy"
                data-testid="contact-map-iframe"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
